import React, { useEffect, useState } from 'react';
import BigNumber from 'bignumber.js';
import { connect } from 'redux-zero/react';
import { useTranslation } from 'react-i18next';
import { useAsync } from 'react-use';
import { get } from 'lodash';
import { Button, LoaderDots } from '@tokenyico/tokeny-ui';
import {
  DsIcon,
  DsButton,
  DsTag,
  DsTooltip,
  DsExplorerLink,
  DsNumber,
} from '@tokenyico/design-system/dist/reactJS';
import { datetimeFormat, dsConfirm, numberFormat } from '@tokenyico/design-system';

import { BillboardService, HolderService } from 'services';
import { formActions } from 'app/state/actions';
import BillboardOfferStatus from './BillboardOfferStatus';
import { InvestorStatusBadge } from 'app/components';

const Loading = () => (
  <div className="_flex:center">
    <LoaderDots />
  </div>
);

const BillboardOfferView = ({
  offer,
  isCreatedByMe,
  onCloseModal,
  setError,
  buyLabel,
  sellLabel,
  selectedToken,
}) => {
  const { t } = useTranslation();

  const network = selectedToken.blockchainNetwork;

  const [isLock, setIsLock] = useState(!isCreatedByMe);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [holderStatus, setHolderStatus] = useState({
    sendingTokenStatus: '',
    receivingTokenStatus: '',
  });
  const [contactInformation, setContactInformation] = useState({
    email: undefined,
    phone: undefined,
    walletAddress: undefined,
  });

  const [sendingToken, setSendingToken] = useState();
  const [receivingToken, setReceivingToken] = useState();

  useEffect(() => {
    if (!selectedOffer) return; // not loaded yet
    if (isCreatedByMe) {
      setSendingToken(selectedOffer.sellingToken);
      setReceivingToken(selectedOffer.buyingToken);
    } else {
      setSendingToken(selectedOffer.buyingToken);
      setReceivingToken(selectedOffer.sellingToken);
    }
  }, [isCreatedByMe, selectedOffer]);

  const state = useAsync(async () => {
    try {
      if (offer) {
        const dataOffer = await BillboardService.getOfferMe(offer.id);
        setSelectedOffer(dataOffer);
      }
    } catch (e) {
      setError(e);
    }
    try {
      if (offer.sellingToken.tokenType !== 'STABLE_COIN') {
        const dataHolder = await HolderService.getTokenHolder(offer.sellingToken.tokenId);
        setHolderStatus({
          ...holderStatus,
          [isCreatedByMe ? 'sendingTokenStatus' : 'receivingTokenStatus']: dataHolder.status,
        });
      }
    } catch (e) {
      //catching nothing because of unreliable data.
    }
    try {
      if (offer.buyingToken.tokenType !== 'STABLE_COIN') {
        const dataHolder = await HolderService.getTokenHolder(offer.buyingToken.tokenId);
        setHolderStatus({
          ...holderStatus,
          [isCreatedByMe ? 'receivingTokenStatus' : 'sendingTokenStatus']: dataHolder.status,
        });
      }
    } catch (e) {
      setError(e);
    }
  }, [offer]);

  const handleUnlockContact = async () => {
    setIsSubmitting(true);
    try {
      const data = await BillboardService.getOfferContact(offer.id);

      setContactInformation({
        email: get(data, 'emailAddress'),
        walletAddress: get(data, 'walletAddress'),
        phone: get(data, 'phoneNumber'),
        other: get(data, 'other'),
      });

      setIsLock(false);
    } catch (error) {
      setError(error);
    }
    setIsSubmitting(false);
  };

  useAsync(async () => {
    try {
      if (!isLock) {
        const data = await BillboardService.getOfferContact(offer.id);

        setContactInformation({
          email: get(data, 'emailAddress'),
          walletAddress: get(data, 'walletAddress'),
          phone: get(data, 'phoneNumber'),
          other: get(data, 'other'),
        });
      }
    } catch (error) {
      setError(error);
    }
  });

  if (!offer) return null;

  const handleCompleteOffer = async () => {
    setIsSubmitting(true);
    try {
      await BillboardService.completeOffer(offer.id);
      onCloseModal();
    } catch (error) {
      setError(error);
    }
    setIsSubmitting(false);
  };

  const handleCancelOffer = async () => {
    try {
      setIsSubmitting(true);
      await BillboardService.cancelOffer(offer.id);
      if (
        await dsConfirm({
          heading: t('shared.cancelOffer'),
          content: t('shared.cancelOfferContent'),
          action: t('shared.cancelOfferAction'),
          version: 'danger',
        })
      ) {
        onCloseModal();
      }
    } catch (error) {
      setError(error);
    }
    setIsSubmitting(false);
  };

  return (
    <div className="offer-details">
      <div className="offer-details__header">
        <h1 className="offer-details__header--title h3"> {t('shared.offerDetails')}</h1>
        <BillboardOfferStatus status={selectedOffer?.status} />
      </div>
      <div className="_text:sm">
        {state.loading || selectedOffer === null ? (
          <Loading />
        ) : (
          <>
            <div className="offer-details__transfer _shadow(1)">
              <div className="offer-details__transfer--sell">
                <div className="offer-details__transfer--sell--token">
                  <p className="_text:xs _text-coolGrey-400 _mbottom-1">{buyLabel}</p>
                  <div className="_flex:align(center)">
                    <div style={{ width: '2.5rem' }}>
                      <img width="100%" src={sendingToken.logo} alt={sendingToken.symbol} />
                    </div>
                    <div className="_flex:column _flex:justify(center) _m:left(3)">
                      <div className="_flex:align(center)">
                        <span className="h6 _m:right(1)">
                          <DsNumber number={sendingToken.amount} currency={sendingToken.symbol} />
                        </span>
                      </div>
                      <span className="_text:xs _text-grey-900">{sendingToken.tokenName}</span>
                    </div>
                  </div>
                  <DsExplorerLink
                    address={sendingToken.tokenAddress}
                    network={network}
                    type="token"
                    width="150px"
                    tooltipPosition="bottom"
                    class="_mtop-1"
                  />
                </div>

                {sendingToken.tokenType !== 'STABLE_COIN' && holderStatus.sendingTokenStatus && (
                  <div className="offer-details__transfer--sell--status">
                    <p className="_text:xs _text-coolGrey-400 _mbottom-1">
                      {t('shared.yourStatus')}
                    </p>
                    <InvestorStatusBadge status={holderStatus.sendingTokenStatus} />
                  </div>
                )}
              </div>

              <div className="transfer-icon _shadow-2">
                <DsIcon name="transfer" size="16px" color="white" />
              </div>

              <div className="offer-details__transfer--buy">
                <div className="offer-details__transfer--sell--token">
                  <p className="_text:xs _text-coolGrey-400 _mbottom-1">{sellLabel}</p>
                  <div className="_flex:align(center)">
                    <div style={{ width: '2.5rem' }}>
                      <img width="100%" src={receivingToken.logo} alt={receivingToken.symbol} />
                    </div>
                    <div className="_flex:column _flex:justify(center) _m:left(3)">
                      <div className="_flex:align(center)">
                        <span className="h6 _m:right(1)">
                          <DsNumber
                            number={receivingToken.amount}
                            currency={receivingToken.symbol}
                          />
                        </span>
                      </div>

                      <span className="_text:xs _text-grey-900">{receivingToken.tokenName}</span>
                    </div>
                  </div>
                  <DsExplorerLink
                    address={receivingToken.tokenAddress}
                    network={network}
                    type="token"
                    width="150px"
                    tooltipPosition="bottom"
                    class="_mtop-1"
                  />
                </div>
                {offer.buyingToken.tokenType !== 'STABLE_COIN' &&
                  holderStatus.receivingTokenStatus && (
                    <div className="offer-details__transfer--sell--status">
                      <p className="_text:xs _text-coolGrey-400 _mbottom-1">
                        {t('shared.yourStatus')}
                      </p>
                      <InvestorStatusBadge status={holderStatus.receivingTokenStatus} />
                    </div>
                  )}
              </div>
            </div>

            <div className="offer-details__other _text:xs">
              <div className="offer-details__other__item">
                <div className="offer-details__other__item--label">{t('shared.price')}</div>
                <div className="offer-details__other__item--value">
                  <>
                    1 {sendingToken.symbol} ={' '}
                    {numberFormat(
                      new BigNumber(receivingToken.amount).dividedBy(sendingToken.amount),
                      3,
                    )}{' '}
                    {receivingToken.symbol}
                  </>
                </div>
              </div>

              <div className="offer-details__other__item _flex-align-center _m:right(2)">
                <div className="offer-details__other__item--label _text:xs  _text-coolGrey-400 _m:right(1)">
                  {t('shared.expirationDate')}
                </div>
                <div className="offer-details__other__item--value">
                  {datetimeFormat(offer.expiryDate)}
                </div>
              </div>
            </div>

            <div className="offer-details__contact">
              <div className="_text:sm _text(500) _m:bottom(2)">{t('shared.contact')}</div>
              <div className="offer-details__contact__info">
                <div className="offer-details__contact__info--label">{t('shared.email')}</div>
                <div className="offer-details__contact__info--self">
                  {isLock ? (
                    <div className="_text-grey-900">
                      <DsIcon name="lock" size="12px" /> ******
                    </div>
                  ) : (
                    <div>{contactInformation.email}</div>
                  )}
                </div>
              </div>

              <div className="offer-details__contact__info">
                <div className="offer-details__contact__info--label">{t('shared.wallet')}</div>
                <div className="offer-details__contact__info--self">
                  {isLock ? (
                    <div className="_text-grey-900">
                      <DsIcon name="lock" size="12px" /> ******
                    </div>
                  ) : (
                    <DsExplorerLink
                      address={contactInformation.walletAddress}
                      network={network}
                      type="wallet"
                    />
                  )}
                </div>
              </div>

              <div className="offer-details__contact__info">
                <div className="offer-details__contact__info--label">{t('shared.phone')}</div>
                <div className="offer-details__contact__info--self">
                  {isLock ? (
                    <div className="_text-grey-900">
                      <DsIcon name="lock" size="12px" /> ******
                    </div>
                  ) : (
                    <div>{contactInformation.phone ?? '-'}</div>
                  )}
                </div>
              </div>

              {!isLock &&
                contactInformation.other &&
                contactInformation.other !== 'undefined: ' && (
                  <div className="offer-details__contact__info">
                    <div className="offer-details__contact__info--label">{t('shared.other')}</div>
                    <div className="offer-details__contact__info--self">
                      <div>{contactInformation.other}</div>
                    </div>
                  </div>
                )}

              {isLock && (
                <div className="offer-details__contact--unlock">
                  <DsTooltip
                    text={t('shared.unlockContactTooltip')}
                    position="top"
                    size="sm"
                    isVisibleOnHover={offer.qualificationStatus !== 'QUALIFIED'}
                  >
                    <Button
                      type="button"
                      version="primary"
                      label={t('shared.unlockContact')}
                      className="_m:top(2)"
                      onClick={handleUnlockContact}
                      isDisabled={isSubmitting || offer.qualificationStatus !== 'QUALIFIED'}
                      isLoading={isSubmitting}
                      icon="Lock"
                      iconHeight={11}
                    />
                  </DsTooltip>
                </div>
              )}
            </div>

            {isCreatedByMe && offer.status === 'INACTIVE' && (
              <div className="_m:bottom(4) _m:x(4)">
                <DsTag
                  label={t('shared.offerInactiveWarning')}
                  version="info"
                  icon="info"
                  size="md"
                />
              </div>
            )}

            {isCreatedByMe && offer.status === 'CANCELLED' && (
              <div className="_m:bottom(4) _m:x(4)">
                <DsTag
                  label={t('shared.offerCancelledWarning')}
                  version="warning"
                  icon="warning"
                  size="md"
                />
              </div>
            )}

            {isCreatedByMe && ['ACTIVE', 'INACTIVE'].includes(selectedOffer.status) && (
              <div className="offer-details__cta-buttons">
                <DsButton
                  className="_m:right(3)"
                  onClick={() => handleCancelOffer()}
                  type="button"
                  version="error"
                  label={t('shared.cancelOffer')}
                  icon="close"
                  isDisabled={
                    isSubmitting || ['COMPLETED', 'CANCELLED'].includes(selectedOffer.status)
                  }
                  isReverse
                  isLoading={isSubmitting}
                />

                <DsButton
                  onClick={() => handleCompleteOffer()}
                  type="button"
                  version="success"
                  label={t('shared.closeAsCompleted')}
                  icon="checkmark"
                  isDisabled={isSubmitting || selectedOffer.status !== 'ACTIVE'}
                  isReverse
                  isLoading={isSubmitting}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const mapToProps = (state) => state.holder;
export default connect(mapToProps, formActions)(BillboardOfferView);
